// Main render pipeline that orchestrates all renderers

/** @typedef {import('../types/jsdoc-types.js').MonsterPlacement} MonsterPlacement */
/** @typedef {import('../types/jsdoc-types.js').MapPlaceable} MapPlaceable */

import { getConeCells } from '../lib/aoe-geometry.js';
import { getBackendUrl } from '../lib/api.js';
import { interactionState } from '../state/interaction-state.js';
import { mapState } from '../state/map-state.js';
import { viewState } from '../state/view-state.js';
import { drawCovers } from './cover-renderer.js';
import { drawDarkvisionGreyscale, drawDimLighting, drawFogOfWar, drawGrid } from './grid-renderer.js';
import { drawFlameIcon } from './icon-rendering.js';
import { loadImage } from './image-cache.js';
import { drawLights } from './light-renderer.js';
import { drawOpenings } from './opening-renderer.js';
import { getTileImage } from './tile-cache.js';
import { drawShadowToken, drawTokenLabels, drawTokens } from './token-renderer.js';
import { drawVisibleWalls, drawWallPreview, drawWalls } from './wall-renderer.js';

/**
 * @typedef {object} RenderOptions
 * @property {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @property {number} canvasWidth - Canvas width
 * @property {number} canvasHeight - Canvas height
 * @property {boolean} [isEditor=false] - Whether rendering in editor mode
 * @property {string} [currentUserId] - Current user's ID
 * @property {boolean} [showGrid=true] - Whether to show grid
 * @property {boolean} [showWalls=true] - Whether to show walls (editor mode)
 * @property {boolean} [showTokens=true] - Whether to show tokens
 * @property {boolean} [showFog=true] - Whether to show fog of war
 * @property {string} [tool] - Current editor tool
 * @property {{col: number, row: number} | null} [mouseGridPosition] - Current mouse grid position
 * @property {number|null} [selectedWallIndex] - Index of selected wall
 * @property {number|null} [hoveredWallIndex] - Index of hovered wall
 * @property {number|null} [selectedOpeningIndex] - Index of selected opening
 * @property {number|null} [hoveredOpeningIndex] - Index of hovered opening
 * @property {number|null} [selectedLightIndex] - Index of selected light
 * @property {number|null} [hoveredLightIndex] - Index of hovered light
 * @property {number|null} [selectedCoverIndex] - Index of selected cover
 * @property {number|null} [hoveredCoverIndex] - Index of hovered cover
 * @property {Array<{x: number, y: number}>|null} [currentCover] - Cover being drawn
 * @property {{x: number, y: number}|null} [coverPreviewPoint] - Cover preview point
 * @property {string} [coverSubtool] - Current cover subtool
 * @property {{x: number, y: number}|null} [lightPreviewPoint] - Light preview point
 * @property {string} [lightSubtool] - Current light subtool
 * @property {{start: {x: number, y: number}, end: {x: number, y: number}}|null} [hoveredWallSegment] - Wall segment being hovered for opening placement
 * @property {Array<{x: number, y: number}>|null} [currentWall] - Wall being drawn (grid coordinates)
 * @property {{x: number, y: number}|null} [wallPreviewPoint] - Wall preview point (pixel coordinates)
 * @property {string} [openingSubtool] - Current opening subtool
 * @property {{zoom: number, panX: number, panY: number}|null} [viewStateOverride] - Override view state for editor
 * @property {boolean} [showNames] - Whether to show token names
 * @property {number|null} [selectedMonsterIndex] - Index of selected monster token
 * @property {number|null} [hoveredMonsterIndex] - Index of hovered monster token
 * @property {{id: string, col: number, row: number} | null} [placementDragOverride] - Override the col/row of a placement while drag-to-move is in progress
 */

/**
 * Apply canvas transforms for zoom and pan
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} zoom
 * @param {number} panX
 * @param {number} panY
 */
function applyTransform(ctx, zoom, panX, panY) {
  ctx.translate(panX, panY);
  ctx.scale(zoom, zoom);
}

/**
 * Clear the canvas
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 */
function clearCanvas(ctx, canvasWidth, canvasHeight) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);
}

/**
 * Draw background image if available
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} gridSize
 * @param {number} gridRows
 * @param {number} gridCols
 * @param {string} backgroundUrl
 */
function drawBackgroundImage(ctx, gridSize, gridRows, gridCols, backgroundUrl) {
  if (!backgroundUrl) return;

  const img = loadImage(backgroundUrl);
  if (img?.complete && img.naturalWidth > 0) {
    const mapWidth = gridCols * gridSize;
    const mapHeight = gridRows * gridSize;
    ctx.drawImage(img, 0, 0, mapWidth, mapHeight);
  }
}

/**
 * Render the game map (player view)
 * @param {RenderOptions} options
 */
export function renderGameMap(options) {
  const {
    ctx,
    canvasWidth,
    canvasHeight,
    currentUserId,
    showGrid = true,
    showWalls = false,
    showTokens = true,
    showFog = true,
  } = options;

  const { zoom, panX, panY } = viewState;
  const {
    gridSize,
    gridRows,
    gridCols,
    backgroundColor,
    backgroundUrl,
    monsterPlacements,
    walls,
    mapVisibility,
    playerCharacters,
    turnState,
  } = mapState;

  // Build the union of PCs and monster placements as drawable items.
  /** @type {Array<*>} */
  const tokens = [];
  for (const pc of playerCharacters) {
    if (pc.col === undefined || pc.row === undefined) continue;
    tokens.push({
      id: pc.id,
      col: pc.col,
      row: pc.row,
      direction: pc.direction || 'down',
      size: 'medium',
      imageUrl: pc.token?.imageUrl,
      name: pc.name,
      playerId: pc.playerId,
      currentHP: pc.hp ?? pc.currentHP,
      maxHP: pc.maxHp ?? pc.maxHP,
      ac: pc.ac,
      hasCircleBorder: pc.token?.hasCircleBorder || false,
      borderColor: pc.token?.borderColor || '#000000',
      conditions: pc.conditions,
      _kind: 'pc',
    });
  }
  for (const mp of monsterPlacements) {
    tokens.push({ ...mp, _kind: 'monster' });
  }

  // Clear and setup
  clearCanvas(ctx, canvasWidth, canvasHeight);

  // Fill background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Apply zoom and pan
  applyTransform(ctx, zoom, panX, panY);

  // Draw background image (single image, if any)
  drawBackgroundImage(ctx, gridSize, gridRows, gridCols, backgroundUrl);

  // Draw per-cell tile images for visible cells
  if (mapVisibility?.gridCells) {
    for (const cell of mapVisibility.gridCells) {
      if (cell.illumination !== 'none' || cell.visibility === 'adjacent') {
        const img = getTileImage(cell.col, cell.row);
        if (img?.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, cell.col * gridSize, cell.row * gridSize, gridSize, gridSize);
        }
      }
    }
  }

  // Draw grid
  if (showGrid) {
    drawGrid(ctx, {
      gridSize,
      gridRows,
      gridCols,
      zoom,
    });
  }

  // Draw placement square highlights BEFORE tokens (so tokens render on top)
  if (interactionState.placement.isPlacing) {
    for (const pos of interactionState.placement.validPositions) {
      const x = pos.col * gridSize;
      const y = pos.row * gridSize;

      // Light blue background fill
      ctx.fillStyle = 'rgba(13, 110, 253, 0.15)';
      ctx.fillRect(x, y, gridSize, gridSize);

      // Blue border
      ctx.strokeStyle = '#0d6efd';
      ctx.lineWidth = 1 / zoom;
      ctx.strokeRect(x, y, gridSize, gridSize);
    }
  }

  // Pre-token attack-targeting pass: desaturate the map tile under
  // each valid target cell, then paint the colored highlight + thin
  // border. Runs BEFORE tokens so the token (and its hover cursor)
  // render ON TOP of the colored cell — token-renderer's cursor stays
  // in plain white over the tint instead of being dimmed by an
  // overlay drawn after it. Token name labels run later in their
  // post-fog pass, also on top.
  if (interactionState.mode === 'attack-targeting' && !interactionState.areaTargeting.isActive) {
    const at = interactionState.attackTargeting;
    const targetSquares = at.targetSquares || [];
    if (targetSquares.length > 0) {
      // Desaturate the tile so the filter-color tint reads as its
      // true color (red/green/blue) regardless of terrain underneath.
      ctx.save();
      ctx.globalCompositeOperation = 'saturation';
      ctx.fillStyle = '#808080';
      for (const sq of targetSquares) {
        ctx.fillRect(sq.col * gridSize, sq.row * gridSize, gridSize, gridSize);
      }
      ctx.restore();

      // Translucent fill + thin border in the filter color. Mirrors
      // the placement-mode treatment so targeting reads in the same
      // vocabulary as PC placement.
      const filter = at.filter;
      const fillColor =
        filter === 'friendly'
          ? 'rgba(22, 163, 74, 0.45)'
          : filter === 'any' || filter === 'selfOrEmpty'
            ? 'rgba(13, 110, 253, 0.45)'
            : 'rgba(220, 38, 38, 0.45)';
      const borderColor =
        filter === 'friendly' ? '#16a34a' : filter === 'any' || filter === 'selfOrEmpty' ? '#0d6efd' : '#dc2626';
      ctx.save();
      ctx.fillStyle = fillColor;
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1 / zoom;
      for (const sq of targetSquares) {
        const x = sq.col * gridSize;
        const y = sq.row * gridSize;
        ctx.fillRect(x, y, gridSize, gridSize);
        ctx.strokeRect(x, y, gridSize, gridSize);
      }
      ctx.restore();
    }
  }

  // Draw tokens (PCs always visible to themselves; monster placements filtered by visibility data)
  // Hoisted outside the `if (showTokens)` block so the label
  // second-pass after fog can re-iterate the same set.
  /** @type {import('../types/jsdoc-types.js').Token[]} */
  let visibleTokens = [];
  if (showTokens) {
    const isPlacing = interactionState.placement.isPlacing;
    visibleTokens = isPlacing
      ? tokens
      : mapVisibility
        ? tokens.filter((t) => t._kind === 'pc' || mapVisibility.monsterPlacements?.some((vt) => vt.id === t.id))
        : tokens;

    // Apply direction drag preview if active
    if (
      interactionState.isDraggingDirection &&
      interactionState.directionDragEntity &&
      interactionState.directionDragPreview
    ) {
      const dragId = interactionState.directionDragEntity.id;
      const dragIdx = visibleTokens.findIndex((t) => t.id === dragId);
      if (dragIdx >= 0) {
        const existing = visibleTokens[dragIdx];
        if (!existing) throw new Error(`Expected visible token at index ${dragIdx}`);
        visibleTokens = [...visibleTokens];
        visibleTokens[dragIdx] = /** @type {*} */ ({
          ...existing,
          direction: /** @type {import('../types/jsdoc-types.js').TokenDirection} */ (
            interactionState.directionDragPreview
          ),
        });
      }
    }

    const combatActive = !!turnState?.combatTime;

    // Cells the viewer's darkvision promoted from darkness/dim — tokens on
    // those cells render in greyscale (SRD 5.2.1: "you can see only in
    // shades of grey"). Pre-compute as a Set for O(1) per-token lookup.
    const darkvisionPromotedCells = new Set(
      (mapVisibility?.gridCells || []).filter((c) => c.perceivedViaDarkvision).map((c) => `${c.col},${c.row}`),
    );

    drawTokens(ctx, visibleTokens, {
      gridSize,
      zoom,
      selectedTokenIndex: interactionState.selectedElements.find((e) => e.type === 'token')
        ? visibleTokens.findIndex((t) => t.id === interactionState.selectedElement?.id)
        : null,
      hoveredTokenIndex:
        interactionState.hover.hoveredElement?.type === 'token'
          ? visibleTokens.findIndex((t) => t.id === interactionState.hover.hoveredElement?.id)
          : null,
      targetableTokenIds:
        interactionState.mode === 'attack-targeting' && !interactionState.areaTargeting.isActive
          ? interactionState.attackTargeting.validTargetIds || []
          : [],
      targetableFilter: interactionState.attackTargeting.filter,
      targetableRangeType: interactionState.attackTargeting.rangeType,
      targetableCursorBase: interactionState.attackTargeting.cursorBase,
      targetableNormalCells: interactionState.attackTargeting.rangeNormalCells,
      targetableLongCells: interactionState.attackTargeting.rangeLongCells,
      targetableAttackerCol: interactionState.attackTargeting.attackerCol,
      targetableAttackerRow: interactionState.attackTargeting.attackerRow,
      targetingActive: interactionState.mode === 'attack-targeting',
      combatActive,
      currentUserId,
      backendUrl: getBackendUrl(),
      characters: playerCharacters,
      showNames: mapState.waitingRoom?.isActive || false,
      // Labels are drawn in a second pass after the fog/darkness
      // overlay so they stay readable in dim/dark cells.
      skipNameLabel: true,
      darkvisionPromotedCells,
    });
  }

  // Draw waiting room seat placeholders for invited (not-yet-joined) users
  if (mapState.waitingRoom?.isActive && mapState.invitedUsers.length > 0) {
    drawWaitingRoomPlaceholders(ctx, gridSize, zoom);
  }

  // Draw fog of war
  if (showFog && mapVisibility?.gridCells) {
    // Draw dim lighting on any cell explicitly marked dim. Even when the map
    // is globally bright, individual cells can be dim — cover-shadowed
    // squares, or cells the viewer's darkvision promoted from darkness.
    drawDimLighting(ctx, mapVisibility.gridCells, gridSize);

    // Greyscale cells whose illumination was promoted by the viewer's
    // darkvision (SRD 5.2.1: "you can see only in shades of grey"). The
    // backend marks them via VisibleGridUnit.PerceivedViaDarkvision.
    drawDarkvisionGreyscale(ctx, mapVisibility.gridCells, gridSize);

    // Draw fog for non-visible cells.
    drawFogOfWar(ctx, mapVisibility.gridCells, gridSize, gridRows, gridCols);

    // Spectator overlay: cells the watched player can't see are included
    // with a `spectatorOnly` flag. Tint them red to distinguish from
    // cells in the player POV. Spectator-only monsters sit on
    // spectator-only cells so the cell tint alone marks them.
    ctx.save();
    ctx.fillStyle = 'rgba(220, 38, 38, 0.35)';
    for (const cell of mapVisibility.gridCells) {
      if (cell.spectatorOnly) {
        ctx.fillRect(cell.col * gridSize, cell.row * gridSize, gridSize, gridSize);
      }
    }
    ctx.restore();
  }

  // Draw visible walls AFTER fog (so they render crisp on top)
  if (showWalls && walls.length > 0) {
    drawVisibleWalls(ctx, walls, { gridSize, zoom, visibleGridCells: mapVisibility?.gridCells });
  }

  // Spell-placed Light overlay: render a flame glyph at any cell where
  // the Light cantrip was cast (Source="spell:light"). Server-side
  // visibility already filters lights to cells the player can see, so
  // we just draw the glyph for every spell-sourced entry in the visible
  // set. Map-placed lights (wall sconces, braziers) intentionally have
  // no glyph here — their visual presence is the illumination cone
  // they cast onto the surrounding cells.
  const visibleLights = mapVisibility?.lights || [];
  for (const light of visibleLights) {
    if (!light || light.source !== 'spell:light') continue;
    const lightX = light.col * gridSize + gridSize / 2;
    const lightY = light.row * gridSize + gridSize / 2;
    drawFlameIcon(ctx, lightX, lightY, gridSize, zoom, light.isOn !== false);
  }

  // Token name labels: second pass after fog/darkness so the
  // pill stays readable when its token sits in a dim or unlit
  // cell. Same hover/showNames condition the first pass uses.
  drawTokenLabels(ctx, visibleTokens, {
    gridSize,
    zoom,
    hoveredTokenIndex:
      interactionState.hover.hoveredElement?.type === 'token'
        ? visibleTokens.findIndex((t) => t.id === interactionState.hover.hoveredElement?.id)
        : null,
    showNames: mapState.waitingRoom?.isActive || false,
  });

  // Single-target targeting mask: dim every cell that isn't a valid
  // target so only colored cells stand out. Covers both range and
  // visibility exclusions — the targetSquares list is server-computed
  // and pre-intersected with both. Skipped for AoE (drawn below).
  if (interactionState.mode === 'attack-targeting' && !interactionState.areaTargeting.isActive) {
    const at = interactionState.attackTargeting;
    const targetSquares = at.targetSquares || [];
    if (targetSquares.length > 0 && gridCols > 0 && gridRows > 0) {
      const valid = new Set(targetSquares.map((s) => `${s.col},${s.row}`));
      // Personal-LOS set, used downstream to flag DIS on a cell the
      // attacker can't actually see (fog/darkness).
      const inLOS = new Set();
      for (const cell of mapVisibility?.gridCells || []) {
        if (cell.visibility === 'inLOS') {
          inLOS.add(`${cell.col},${cell.row}`);
        }
      }
      // The colored cell highlight + border was painted in the
      // pre-token pass at the top of this function so token + cursor
      // render on top of it. Nothing to do here for valid cells; we
      // only need the cell-cursor for empty / placeholder cells, and
      // the ban-cursor for invalid cells, both handled below.

      // Hover cursor over a cell. Three cases, in order of likelihood:
      //   1. Real (visible) token — drawToken handles the targeting
      //      indicator and cursor; nothing to do here.
      //   2. Empty cell or placeholder ("shape in darkness") in the
      //      valid set — draw the swords/target cursor + optional DIS.
      //   3. Cell outside the valid set — draw the ban cursor + a
      //      reason badge (RANGE / NO LOS / SELF).
      const hoveredCol = interactionState.hover.col;
      const hoveredRow = interactionState.hover.row;
      const hoveredEl = interactionState.hover.hoveredElement;
      const hoveredTokenObj = hoveredEl?.type === 'token' ? visibleTokens.find((t) => t.id === hoveredEl.id) : null;
      const hoveringRealToken = !!hoveredTokenObj && !hoveredTokenObj.placeholder;
      if (!hoveringRealToken && hoveredCol !== null && hoveredRow !== null) {
        const aCol = at.attackerCol;
        const aRow = at.attackerRow;
        const normal = at.rangeNormalCells;
        const long = at.rangeLongCells;
        const isMelee = at.rangeType === 'reach';
        const dist =
          aCol != null && aRow != null ? Math.max(Math.abs(hoveredCol - aCol), Math.abs(hoveredRow - aRow)) : null;
        const maxRange = long ?? normal;
        const isValidCell = valid.has(`${hoveredCol},${hoveredRow}`);

        let iconName;
        let badgeLabel = null;
        let badgeColor = '#dc3545';

        if (isValidCell) {
          // Cursor itself is white; the cell highlight under it
          // carries the filter color.
          iconName = `${at.cursorBase}-white`;
          // Disadvantage triggers: long-range band (ranged only), a
          // placeholder, or a cell in fog/darkness. Lit empty cells in
          // clear LOS show no badge.
          const inLongRange =
            !isMelee &&
            normal != null &&
            long != null &&
            aCol != null &&
            aRow != null &&
            long > normal &&
            dist != null &&
            dist > normal;
          const isPlaceholder = !!hoveredTokenObj && hoveredTokenObj.placeholder;
          const cellInDarkness = !inLOS.has(`${hoveredCol},${hoveredRow}`);
          if (inLongRange || isPlaceholder || cellInDarkness) {
            badgeLabel = 'DIS';
          }
        } else {
          iconName = 'ban-gray';
          if (dist === 0) {
            badgeLabel = 'SELF';
          } else if (dist != null && maxRange != null && dist > maxRange) {
            badgeLabel = 'RANGE';
          } else {
            badgeLabel = 'NO LOS';
          }
          badgeColor = '#525252';
        }

        const icon = iconName ? loadImage(`/assets/icons/${iconName}.png`) : null;
        if (icon?.complete && icon.naturalWidth > 0) {
          const iconSize = gridSize * 0.7;
          const cx = (hoveredCol + 0.5) * gridSize;
          const cy = (hoveredRow + 0.5) * gridSize;
          ctx.save();
          ctx.drawImage(icon, cx - iconSize / 2, cy - iconSize / 2, iconSize, iconSize);
          ctx.restore();

          if (badgeLabel) {
            const fontPx = Math.max(8, gridSize * 0.16);
            ctx.save();
            ctx.font = `700 ${fontPx}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const padX = fontPx * 0.45;
            const padY = fontPx * 0.2;
            const textWidth = ctx.measureText(badgeLabel).width;
            const badgeW = textWidth + padX * 2;
            const badgeH = fontPx + padY * 2;
            const cellBottom = (hoveredRow + 1) * gridSize;
            const badgeMargin = Math.max(2, gridSize * 0.04);
            const badgeY = cellBottom - badgeH - badgeMargin;
            const badgeX = cx - badgeW / 2;
            const radius = badgeH / 2;
            ctx.fillStyle = badgeColor;
            ctx.beginPath();
            ctx.moveTo(badgeX + radius, badgeY);
            ctx.arcTo(badgeX + badgeW, badgeY, badgeX + badgeW, badgeY + badgeH, radius);
            ctx.arcTo(badgeX + badgeW, badgeY + badgeH, badgeX, badgeY + badgeH, radius);
            ctx.arcTo(badgeX, badgeY + badgeH, badgeX, badgeY, radius);
            ctx.arcTo(badgeX, badgeY, badgeX + badgeW, badgeY, radius);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.fillText(badgeLabel, cx, badgeY + badgeH / 2);
            ctx.restore();
          }
        }
      }
    }
  }

  // Draw AoE targeting template
  if (interactionState.mode === 'attack-targeting' && interactionState.areaTargeting.isActive) {
    const hoveredCol = interactionState.hover.col;
    const hoveredRow = interactionState.hover.row;
    const areaTargeting = interactionState.areaTargeting;
    const isInRange =
      hoveredCol !== null &&
      hoveredRow !== null &&
      areaTargeting.targetSquares.some(
        /** @param {{col: number, row: number}} s */
        (s) => s.col === hoveredCol && s.row === hoveredRow,
      );

    // Filter drives the color: friendly → green (healing/buff), else red.
    const filter = interactionState.attackTargeting.filter;
    const healing = filter === 'friendly';
    const fillColor = healing ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)';
    const strokeColor = healing ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
    const rangeFill = healing ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)';
    const rangeStroke = healing ? 'rgba(34, 197, 94, 0.35)' : 'rgba(239, 68, 68, 0.35)';

    // Subtle overlay on every in-range cell so the player sees where the AoE
    // can be centered, without the affected footprint yet.
    ctx.save();
    for (const square of areaTargeting.targetSquares) {
      const x = square.col * gridSize;
      const y = square.row * gridSize;
      ctx.fillStyle = rangeFill;
      ctx.fillRect(x, y, gridSize, gridSize);
      ctx.strokeStyle = rangeStroke;
      ctx.lineWidth = 1 / zoom;
      ctx.strokeRect(x, y, gridSize, gridSize);
    }
    ctx.restore();

    if (isInRange && hoveredCol !== null && hoveredRow !== null) {
      const areaSizeInCells = (areaTargeting.areaSize || 5) / 5;
      /** @type {Array<{col: number, row: number}>} */
      let affectedCells = [];
      if (areaTargeting.areaShape === 'cone') {
        // Cone apex = caster's cell; player aims by hovering a cell
        // within range, and the cone snaps to the nearest of 8 grid
        // directions. Match the backend resolver in spell_targeting.go.
        const attackerId = interactionState.attackTargeting.attackerId;
        const caster = playerCharacters.find((c) => c.id === attackerId);
        if (caster && caster.col !== undefined && caster.row !== undefined) {
          affectedCells = getConeCells(
            { col: caster.col, row: caster.row },
            { col: hoveredCol, row: hoveredRow },
            areaSizeInCells,
          );
        }
      } else {
        // sphere (and cube fallback): Chebyshev disk around the hover.
        for (let dc = -areaSizeInCells; dc <= areaSizeInCells; dc++) {
          for (let dr = -areaSizeInCells; dr <= areaSizeInCells; dr++) {
            const dist = Math.max(Math.abs(dc), Math.abs(dr));
            if (dist <= areaSizeInCells) {
              affectedCells.push({ col: hoveredCol + dc, row: hoveredRow + dr });
            }
          }
        }
      }

      ctx.save();
      for (const cell of affectedCells) {
        const x = cell.col * gridSize;
        const y = cell.row * gridSize;
        ctx.fillStyle = fillColor;
        ctx.fillRect(x, y, gridSize, gridSize);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 2 / zoom;
        ctx.strokeRect(x, y, gridSize, gridSize);
      }
      ctx.restore();
    }
  }

  // Draw movement trail
  if (interactionState.isMoving && interactionState.movementTrail.length >= 2) {
    const trail = interactionState.movementTrail;
    const trailColor = '#22c55e';

    ctx.save();
    ctx.strokeStyle = trailColor;
    ctx.lineWidth = 6 / zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const trailStart = trail[0];
    if (!trailStart) throw new Error('Expected trail start point');
    ctx.beginPath();
    ctx.moveTo(trailStart.x, trailStart.y);
    for (let i = 1; i < trail.length; i++) {
      const point = trail[i];
      if (!point) continue;
      ctx.lineTo(point.x, point.y);
    }
    ctx.stroke();

    // Dots at each grid position
    ctx.fillStyle = trailColor;
    for (const point of trail) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 4 / zoom, 0, 2 * Math.PI);
      ctx.fill();
    }
    ctx.restore();
  }

  // Draw movement shadow token (destination preview)
  if (interactionState.isMoving && interactionState.movementPreview) {
    const preview = interactionState.movementPreview;
    const movingToken = tokens.find((t) => t.id === interactionState.movingEntity?.id);
    if (movingToken) {
      drawShadowToken(
        ctx,
        {
          ...movingToken,
          col: preview.col,
          row: preview.row,
          direction: /** @type {import('../types/jsdoc-types.js').TokenDirection} */ (preview.direction),
        },
        {
          gridSize,
          zoom,
        },
      );
    }
  }

  // Draw placement shadow token AFTER fog (so it's visible on top)
  if (interactionState.placement.isPlacing) {
    const shadow = interactionState.placement.shadowToken;
    if (shadow) {
      const shadowChar = /** @type {import('../types/jsdoc-types.js').Character|undefined} */ (shadow.character);
      const imgUrl = shadowChar?.token?.imageUrl;
      if (imgUrl) {
        const img = loadImage(imgUrl);
        if (img?.complete && img.naturalWidth > 0) {
          const tokenX = shadow.col * gridSize;
          const tokenY = shadow.row * gridSize;
          ctx.save();
          ctx.globalAlpha = 1.0;
          ctx.drawImage(img, tokenX, tokenY, gridSize, gridSize);
          ctx.restore();
        }
      }
    }
  }

  // Draw remote players' placement hover ghosts. Layout per cell:
  //   - solo at a cell: no offset (centered on the cell, like a token)
  //   - sharing a cell with our own shadow: every ghost is offset
  //     (own shadow always wins center)
  //   - multiple ghosts at the same cell: 1st ghost centered (unless
  //     own shadow is there), additional ghosts fan out in alternating
  //     directions
  if (interactionState.remotePlacementHovers.size > 0) {
    /** @type {Map<string, Array<{playerId: string, hover: any, character: any}>>} */
    const byCell = new Map();
    for (const [playerId, hover] of interactionState.remotePlacementHovers) {
      const placedPC = playerCharacters.find(
        (pc) => pc.playerId === playerId && pc.col !== undefined && pc.row !== undefined,
      );
      if (placedPC) continue;
      const character = playerCharacters.find((c) => c.id === hover.characterId);
      if (!character?.token?.imageUrl) continue;
      const key = `${hover.col},${hover.row}`;
      if (!byCell.has(key)) byCell.set(key, []);
      /** @type {Array<{playerId: string, hover: any, character: any}>} */
      (byCell.get(key)).push({ playerId, hover, character });
    }

    const ownShadow = interactionState.placement.shadowToken;
    const ownKey = ownShadow ? `${ownShadow.col},${ownShadow.row}` : null;
    const offsetStep = gridSize * 0.35;
    /** @type {Array<{dx: number, dy: number}>} */
    const offsetDirs = [
      { dx: 1, dy: 0 }, // right
      { dx: -1, dy: 0 }, // left
      { dx: 0, dy: 1 }, // down
      { dx: 0, dy: -1 }, // up
      { dx: 1, dy: 1 }, // down-right
      { dx: -1, dy: 1 }, // down-left
      { dx: 1, dy: -1 }, // up-right
      { dx: -1, dy: -1 }, // up-left
    ];

    for (const [key, items] of byCell) {
      // Stable order so the same player always lands at the same offset slot.
      items.sort((a, b) => a.playerId.localeCompare(b.playerId));
      const ownTaken = key === ownKey;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item) continue;
        const img = loadImage(item.character.token.imageUrl);
        if (!img || !img.complete || img.naturalWidth === 0) continue;
        // Rank 0 means "centered". If our own shadow already occupies
        // this cell, every ghost shifts up one rank.
        const rank = ownTaken ? i + 1 : i;
        let dx = 0;
        let dy = 0;
        if (rank > 0) {
          const dir = offsetDirs[(rank - 1) % offsetDirs.length];
          if (dir) {
            dx = dir.dx * offsetStep;
            dy = dir.dy * offsetStep;
          }
        }
        const tokenX = item.hover.col * gridSize + dx;
        const tokenY = item.hover.row * gridSize + dy;
        ctx.save();
        ctx.globalAlpha = 0.5;
        ctx.drawImage(img, tokenX, tokenY, gridSize, gridSize);
        ctx.restore();
      }
    }
  }
}

/**
 * Draw placeholder circles at empty waiting room seats for invited users
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} gridSize
 * @param {number} zoom
 */
function drawWaitingRoomPlaceholders(ctx, gridSize, zoom) {
  const waitingRoom = mapState.waitingRoom;
  if (!waitingRoom) return;

  // Find empty seats (not yet occupied)
  const emptySeats = waitingRoom.seats.filter((seat) => !seat.playerId);

  for (const seat of emptySeats) {
    const centerX = seat.position.col * gridSize + gridSize / 2 + (seat.offsetX || 0);
    const centerY = seat.position.row * gridSize + gridSize / 2 + (seat.offsetY || 0);
    const radius = gridSize * 0.35;

    ctx.save();

    // Draw circle background
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(60, 60, 60, 0.8)';
    ctx.fill();

    // Draw dashed border
    ctx.strokeStyle = '#888';
    ctx.lineWidth = 2 / zoom;
    ctx.setLineDash([4 / zoom, 4 / zoom]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw question mark icon
    ctx.fillStyle = '#aaa';
    ctx.font = `bold ${radius * 0.9}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('?', centerX, centerY);

    ctx.restore();
  }
}

/**
 * Render the map editor
 * @param {RenderOptions} options
 */
export function renderMapEditor(options) {
  const {
    ctx,
    canvasWidth,
    canvasHeight,
    showGrid = true,
    showWalls = true,
    showTokens = true,
    tool = 'select',
    mouseGridPosition = null,
    selectedWallIndex = null,
    hoveredWallIndex = null,
    selectedOpeningIndex = null,
    hoveredOpeningIndex = null,
    selectedLightIndex = null,
    hoveredLightIndex = null,
    selectedCoverIndex = null,
    hoveredCoverIndex = null,
    currentCover = null,
    coverPreviewPoint = null,
    coverSubtool = 'selectCover',
    lightPreviewPoint = null,
    lightSubtool = 'selectLight',
    hoveredWallSegment = null,
    currentWall = null,
    wallPreviewPoint = null,
    openingSubtool = 'selectOpening',
    viewStateOverride = null,
    selectedMonsterIndex = null,
    hoveredMonsterIndex = null,
    /**
     * While drag-to-move is in progress, render the matching placement
     * at this col/row instead of its persisted position. Caller dispatches
     * the persistent move on mouseup.
     */
    placementDragOverride = null,
  } = options;

  const { zoom, panX, panY } = viewStateOverride || viewState;
  const {
    gridSize,
    gridRows,
    gridCols,
    backgroundColor,
    backgroundUrl,
    monsterPlacements: tokens,
    walls,
    lights,
    openings,
    covers,
    pcStartingPositions,
  } = mapState;

  // Clear and setup
  clearCanvas(ctx, canvasWidth, canvasHeight);

  // Fill background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Apply zoom and pan
  applyTransform(ctx, zoom, panX, panY);

  // Draw background image
  drawBackgroundImage(ctx, gridSize, gridRows, gridCols, backgroundUrl);

  // Draw grid
  if (showGrid) {
    drawGrid(ctx, {
      gridSize,
      gridRows,
      gridCols,
      zoom,
    });
  }

  // Draw PC starting positions
  const isPCTool = tool === 'setPCStartingPositions';
  for (const pos of pcStartingPositions) {
    const col = pos.col ?? pos.position?.col;
    const row = pos.row ?? pos.position?.row;
    if (col === undefined || row === undefined) continue;
    ctx.fillStyle = '#3b82f6';
    ctx.globalAlpha = 0.5;
    ctx.fillRect(col * gridSize, row * gridSize, gridSize, gridSize);
    ctx.globalAlpha = 1.0;
    if (isPCTool) {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3 / zoom;
      ctx.strokeRect(col * gridSize, row * gridSize, gridSize, gridSize);
    }
  }

  // Draw PC starting position preview square on hover
  if (isPCTool && mouseGridPosition) {
    const { col, row } = mouseGridPosition;
    ctx.fillStyle = '#3b82f6';
    ctx.globalAlpha = 0.3;
    ctx.fillRect(col * gridSize, row * gridSize, gridSize, gridSize);
    ctx.globalAlpha = 1.0;
    ctx.setLineDash([10, 5]);
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2 / zoom;
    ctx.strokeRect(col * gridSize, row * gridSize, gridSize, gridSize);
    ctx.setLineDash([]);
  }

  // Draw lights
  drawLights(ctx, lights, {
    gridSize,
    zoom,
    tool,
    selectedLightIndex,
    hoveredLightIndex,
    lightPreviewPoint,
    lightSubtool,
  });

  // Draw walls
  if (showWalls) {
    drawWalls(ctx, walls, {
      gridSize,
      zoom,
      isEditorMode: true,
      selectedWallIndex,
      hoveredWallIndex,
      showWalls: true,
    });

    // Draw wall preview if drawing
    if (currentWall && currentWall.length > 0) {
      drawWallPreview(ctx, currentWall, wallPreviewPoint, {
        gridSize,
        zoom,
      });
    } else if (wallPreviewPoint) {
      // Draw standalone preview point when no wall is being drawn yet
      ctx.fillStyle = '#ff0000';
      ctx.globalAlpha = 0.6;
      const circleRadius = 8 / zoom;
      ctx.beginPath();
      ctx.arc(wallPreviewPoint.x, wallPreviewPoint.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    }
  }

  // Draw openings (doors, windows, openings)
  drawOpenings(ctx, openings, {
    gridSize,
    zoom,
    tool,
    showWalls,
    selectedOpeningIndex,
    hoveredOpeningIndex,
    hoveredWallSegment,
    openingSubtool,
  });

  // Draw covers above grid only when in drawCover tool
  if (tool === 'drawCover') {
    drawCovers(ctx, covers, {
      gridSize,
      zoom,
      tool,
      selectedCoverIndex,
      hoveredCoverIndex,
      currentCover,
      coverPreviewPoint,
      coverSubtool,
    });
  }

  // Draw tokens
  if (showTokens) {
    // Apply direction drag preview if active
    let visibleTokens = tokens;
    if (
      interactionState.isDraggingDirection &&
      interactionState.directionDragEntity &&
      interactionState.directionDragPreview
    ) {
      const dragId = interactionState.directionDragEntity.id;
      const dragIdx = tokens.findIndex((t) => t.id === dragId);
      const existing = tokens[dragIdx];
      if (dragIdx >= 0 && existing) {
        visibleTokens = [...tokens];
        visibleTokens[dragIdx] = /** @type {MonsterPlacement} */ ({
          ...existing,
          direction: /** @type {import('../types/jsdoc-types.js').TokenDirection} */ (
            interactionState.directionDragPreview
          ),
        });
      }
    }

    if (placementDragOverride) {
      const idx = visibleTokens.findIndex((t) => t.id === placementDragOverride.id);
      if (idx >= 0) {
        const existing = visibleTokens[idx];
        if (existing) {
          visibleTokens = [...visibleTokens];
          visibleTokens[idx] = /** @type {MonsterPlacement} */ ({
            ...existing,
            col: placementDragOverride.col,
            row: placementDragOverride.row,
          });
        }
      }
    }

    drawTokens(ctx, visibleTokens, {
      gridSize,
      zoom,
      selectedTokenIndex: selectedMonsterIndex,
      hoveredTokenIndex: hoveredMonsterIndex,
      backendUrl: getBackendUrl(),
    });
  }

  // Draw hover highlight
  if (interactionState.hover.col !== null && interactionState.hover.row !== null) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(interactionState.hover.col * gridSize, interactionState.hover.row * gridSize, gridSize, gridSize);
  }
}
